import React, {useState} from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  alpha,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import CloseIcon from '@mui/icons-material/Close';

import {useAuth0} from "@auth0/auth0-react";
import {Link} from "react-router-dom";
import LangSwitcher from "components/LangSwitcher";
import GoogleLoginButton from "components/GoogleLoginButton";
import {useTranslation} from "react-i18next";

const Header = () => {
  const {loginWithRedirect} = useAuth0();
  const {t} = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    {label: t('Features'), href: '#features'},
    {label: t('Pricing'), href: '#pricing'},
    {label: t('FAQ'), href: '#faq'},
    {label: t('Demo'), href: 'https://qrseller-client.netlify.app/?restaurant_id=2', external: true},
  ];

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.default, 0.8),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Toolbar sx={{py: 1}}>
          {/* Logo */}
          <Box 
            sx={{
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1,
              cursor: 'pointer',
            }}
            onClick={scrollToTop}
          >
            <FastfoodIcon sx={{mr: 1, color: 'primary.main', fontSize: 28}} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 800,
                color: 'primary.main',
                letterSpacing: '-0.01em',
              }}
            >
              QRseller
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  href={item.href}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  sx={{
                    color: 'text.primary',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}

              <Box sx={{mx: 1}}>
                <LangSwitcher />
              </Box>

              <GoogleLoginButton sx={{mr: 1}} />

              <Link to="/" onClick={() => loginWithRedirect()}>
                <Button
                  sx={{
                    color: 'text.primary',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {t("Login")}
                </Button>
              </Link>

              <Link
                to="/"
                onClick={() => loginWithRedirect({screen_hint: 'signup'})}
                style={{textDecoration: 'none'}}
              >
                <Button
                  variant="contained"
                  sx={{
                    ml: 1,
                    px: 3,
                    borderRadius: '10px',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  {t("Sign Up")}
                </Button>
              </Link>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <LangSwitcher />
              <IconButton
                edge="end"
                color="primary"
                onClick={handleMobileMenuToggle}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        PaperProps={{
          sx: {
            width: '80%',
            maxWidth: 320,
            p: 2,
          },
        }}
      >
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
          <IconButton onClick={handleMobileMenuToggle}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List>
          {menuItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                component="a"
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                onClick={handleMobileMenuToggle}
                sx={{
                  borderRadius: '8px',
                  mb: 1,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Typography sx={{fontWeight: 500}}>{item.label}</Typography>
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{mt: 4, display: 'flex', flexDirection: 'column', gap: 2}}>
          <GoogleLoginButton fullWidth onClick={handleMobileMenuToggle} />

          <Link to="/" onClick={() => loginWithRedirect()} style={{textDecoration: 'none'}}>
            <Button
              fullWidth
              variant="outlined"
              sx={{borderRadius: '10px'}}
            >
              {t("Login")}
            </Button>
          </Link>

          <Link
            to="/"
            onClick={() => loginWithRedirect({screen_hint: 'signup'})}
            style={{textDecoration: 'none'}}
          >
            <Button
              fullWidth
              variant="contained"
              sx={{
                borderRadius: '10px',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {t("Sign Up")}
            </Button>
          </Link>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;